import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authApi, invitesApi, organizationsApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

// Business categories for compliance
const BUSINESS_CATEGORIES = [
  { value: 'fashion', label: 'Fashion & Apparel' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'technology', label: 'Technology & Software' },
  { value: 'finance', label: 'Finance & Business Services' },
  { value: 'home_garden', label: 'Home & Garden' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'travel', label: 'Travel & Lifestyle' },
  { value: 'education', label: 'Education & Courses' },
  { value: 'beauty', label: 'Beauty & Personal Care' },
  { value: 'sports', label: 'Sports & Outdoors' },
  { value: 'entertainment', label: 'Entertainment & Media' },
  { value: 'services', label: 'Professional Services' },
  { value: 'ecommerce', label: 'E-commerce & Retail' },
  { value: 'saas', label: 'SaaS & Subscriptions' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'other', label: 'Other' },
];

interface InviteInfo {
  inviteCode: string;
  organizationName: string;
  email?: string;
}

interface OrgSignupInfo {
  organizationId: string;
  organizationName: string;
  slug: string;
  logo?: string;
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('invite');
  const orgSlug = searchParams.get('org');
  const { isAuthenticated, user, isLoading: authLoading } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    businessCategory: '',
    accountType: 'affiliate' as 'affiliate' | 'organization',
    acceptedTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [orgSignupInfo, setOrgSignupInfo] = useState<OrgSignupInfo | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(!!inviteCode || !!orgSlug);

  // Redirect logged-in users to the appropriate join flow
  useEffect(() => {
    if (authLoading) return; // Wait for auth to be determined

    if (isAuthenticated && user) {
      // If user is an affiliate and has an invite or org to join, redirect them
      if (user.role === 'affiliate') {
        if (inviteCode) {
          navigate(`/invite/${inviteCode}`);
          return;
        }
        if (orgSlug) {
          navigate(`/join/${orgSlug}`);
          return;
        }
        // No invite/org, redirect to dashboard
        navigate('/dashboard');
      } else if (user.role === 'org_admin') {
        // Org admins can't join as affiliates
        if (inviteCode || orgSlug) {
          toast.error('Organization accounts cannot join affiliate programs. Please log out and create an affiliate account.');
        }
        navigate('/org');
      } else {
        // Other roles, redirect to home
        navigate('/');
      }
    }
  }, [authLoading, isAuthenticated, user, inviteCode, orgSlug, navigate]);

  // Fetch invite details if invite code is present
  useEffect(() => {
    const fetchInvite = async () => {
      if (!inviteCode) return;

      try {
        const response = await invitesApi.getByCode(inviteCode);
        if (response.data.success) {
          const { invite, organization } = response.data.data;
          setInviteInfo({
            inviteCode,
            organizationName: organization.name,
            email: invite.email,
          });
          // Pre-fill email if provided in invite
          if (invite.email) {
            setFormData((prev) => ({ ...prev, email: invite.email }));
          }
          // Force affiliate account type for invites
          setFormData((prev) => ({ ...prev, accountType: 'affiliate' }));
        }
      } catch {
        // Invalid or expired invite - allow normal registration
        toast.error('The invite link is invalid or has expired. You can still register normally.');
      } finally {
        setIsLoadingInvite(false);
      }
    };

    fetchInvite();
  }, [inviteCode]);

  // Fetch organization details if org slug is present
  useEffect(() => {
    const fetchOrg = async () => {
      if (!orgSlug || inviteCode) return; // Skip if we have an invite code (it takes priority)

      try {
        const response = await organizationsApi.getBySlug(orgSlug);
        if (response.data.success) {
          const { organization } = response.data.data;
          setOrgSignupInfo({
            organizationId: organization.organizationId,
            organizationName: organization.name,
            slug: organization.slug,
            logo: organization.logo,
          });
          // Force affiliate account type for org signup
          setFormData((prev) => ({ ...prev, accountType: 'affiliate' }));
        }
      } catch {
        // Invalid org slug - allow normal registration
        toast.error('The organization was not found. You can still register normally.');
      } finally {
        setIsLoadingInvite(false);
      }
    };

    fetchOrg();
  }, [orgSlug, inviteCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
    setFormData((prev) => ({
      ...prev,
      [target.name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.acceptedTerms) {
      toast.error('You must accept the Terms of Service to continue');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (formData.accountType === 'organization' && !formData.businessCategory) {
      toast.error('Please select your business category');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        accountType: formData.accountType,
        organizationName: formData.accountType === 'organization' ? formData.organizationName : undefined,
        businessCategory: formData.accountType === 'organization' ? formData.businessCategory : undefined,
        acceptedTerms: formData.acceptedTerms,
        // Send orgSlug so affiliate can be auto-created during registration
        orgSlug: orgSignupInfo?.slug,
      });

      if (response.data.success) {
        const affiliateCreated = response.data.data?.affiliate;

        if (affiliateCreated) {
          // Affiliate was auto-created during registration
          const statusMessage = affiliateCreated.status === 'approved'
            ? `You've joined ${affiliateCreated.organizationName}'s affiliate program!`
            : `Your application to join ${affiliateCreated.organizationName} has been submitted.`;
          toast.success(`Account created! ${statusMessage} Please check your email for a verification code.`);
        } else {
          toast.success('Account created! Please check your email for a verification code.');
        }

        // Build verification redirect URL
        let verifyUrl = `/verify-email?email=${encodeURIComponent(formData.email)}`;

        // If affiliate was auto-created, no need to go to /join page after login
        // Only add invite/org param if we still need to process something after verification
        if (inviteInfo) {
          verifyUrl += `&invite=${inviteInfo.inviteCode}`;
        } else if (orgSignupInfo && !affiliateCreated) {
          // Only redirect to /join if affiliate wasn't already created
          verifyUrl += `&org=${orgSignupInfo.slug}`;
        }

        navigate(verifyUrl);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string; details?: Record<string, string> } } } };
      const details = err.response?.data?.error?.details;
      if (details) {
        Object.values(details).forEach((msg) => toast.error(msg));
      } else {
        toast.error(err.response?.data?.error?.message || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || isLoadingInvite) {
    return (
      <div className="animate-fade-in flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="lg:hidden mb-8 text-center">
        <Link to="/" className="font-display text-2xl font-bold text-charcoal tracking-tight">
          venndela
        </Link>
        <p className="text-charcoal/60 mt-1">Turn customers into advocates</p>
      </div>

      {inviteInfo ? (
        <>
          <h2 className="font-serif text-3xl text-charcoal mb-2">Join as an Affiliate</h2>
          <div className="bg-sage/10 border border-sage/30 rounded-lg p-4 mb-6">
            <p className="text-charcoal/80 text-sm">
              You&apos;ve been invited to join <strong>{inviteInfo.organizationName}</strong>&apos;s affiliate program.
              Create your account to get started.
            </p>
          </div>
        </>
      ) : orgSignupInfo ? (
        <>
          <h2 className="font-serif text-3xl text-charcoal mb-2">Become an Affiliate</h2>
          <div className="bg-sage/10 border border-sage/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              {orgSignupInfo.logo ? (
                <img
                  src={orgSignupInfo.logo}
                  alt={orgSignupInfo.organizationName}
                  className="w-10 h-10 rounded-full object-cover bg-white"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-rust flex items-center justify-center">
                  <span className="text-lg font-bold text-white">
                    {orgSignupInfo.organizationName.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <p className="text-charcoal/80 text-sm">
                  Join <strong>{orgSignupInfo.organizationName}</strong>&apos;s affiliate program.
                </p>
                <p className="text-charcoal/60 text-xs mt-0.5">
                  Create your account to start earning commissions.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <h2 className="font-serif text-3xl text-charcoal mb-2">Create an account</h2>
          <p className="text-charcoal/60 mb-8">Join the affiliate platform</p>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="label">First name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              className="input"
              placeholder="John"
              required
            />
          </div>
          <div>
            <label htmlFor="lastName" className="label">Last name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              className="input"
              placeholder="Doe"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            className="input"
            placeholder="you@example.com"
            required
          />
        </div>

        {/* Hide account type selector when registering via invite or org signup URL */}
        {!inviteInfo && !orgSignupInfo && (
          <div>
            <label htmlFor="accountType" className="label">Account type</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, accountType: 'affiliate' }))}
                className={`p-4 text-left border transition-all ${
                  formData.accountType === 'affiliate'
                    ? 'border-rust bg-rust/5'
                    : 'border-tan/50 hover:border-tan'
                }`}
              >
                <span className="font-display font-bold text-charcoal block mb-1">Affiliate</span>
                <span className="text-charcoal/60 text-sm">Promote products and earn commissions</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, accountType: 'organization' }))}
                className={`p-4 text-left border transition-all ${
                  formData.accountType === 'organization'
                    ? 'border-rust bg-rust/5'
                    : 'border-tan/50 hover:border-tan'
                }`}
              >
                <span className="font-display font-bold text-charcoal block mb-1">Organization</span>
                <span className="text-charcoal/60 text-sm">Run your own affiliate program</span>
              </button>
            </div>
          </div>
        )}

        {formData.accountType === 'organization' && (
          <>
            <div>
              <label htmlFor="organizationName" className="label">Organization name</label>
              <input
                id="organizationName"
                name="organizationName"
                type="text"
                autoComplete="organization"
                value={formData.organizationName}
                onChange={handleChange}
                className="input"
                placeholder="Your Company Name"
                required
              />
            </div>
            <div>
              <label htmlFor="businessCategory" className="label">Business category</label>
              <select
                id="businessCategory"
                name="businessCategory"
                value={formData.businessCategory}
                onChange={handleChange}
                className="input"
                required
              >
                <option value="">Select your industry</option>
                {BUSINESS_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-charcoal/50 mt-1">
                This helps us ensure compliance with payment processing requirements
              </p>
            </div>
          </>
        )}

        <div>
          <label htmlFor="password" className="label">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            className="input"
            placeholder="At least 8 characters"
            required
            minLength={8}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="label">Confirm password</label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="input"
            placeholder="Confirm your password"
            required
          />
        </div>

        <div className="flex items-start gap-3">
          <input
            id="acceptedTerms"
            name="acceptedTerms"
            type="checkbox"
            checked={formData.acceptedTerms}
            onChange={handleChange}
            className="mt-1 h-4 w-4 rounded border-tan/50 text-rust focus:ring-rust"
            required
          />
          <label htmlFor="acceptedTerms" className="text-sm text-charcoal/70 leading-relaxed">
            I agree to the{' '}
            <Link to="/legal/terms" target="_blank" className="text-rust hover:text-rust-light underline">
              Terms of Service
            </Link>
            ,{' '}
            <Link to="/legal/privacy" target="_blank" className="text-rust hover:text-rust-light underline">
              Privacy Policy
            </Link>
            , and{' '}
            <Link to="/legal/acceptable-use" target="_blank" className="text-rust hover:text-rust-light underline">
              Acceptable Use Policy
            </Link>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading || !formData.acceptedTerms}
          className="btn btn-primary w-full py-3"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Creating account...
            </span>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-charcoal/60">
        Already have an account?{' '}
        <Link to="/login" className="text-rust hover:text-rust-light font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
